import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-dialog-confirm',
  standalone: true,
  imports: [
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatIconModule,
    CommonModule
  ],
  styles: [`
    .dialog-container {
      padding: 24px;
      border-radius: 8px;
      background-color: var(--form-card-bg);
    }

    .dialog-header {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
      color: var(--form-text-primary);
      font-size: 22px;
      font-weight: 500;
    }

    .dialog-icon {
      margin-right: 12px;
    }

    .icon-info {
      color: var(--dashboard-info-color);
    }

    .icon-warning {
      color: var(--form-warning-color);
    }

    .icon-error {
      color: var(--form-error-color);
    }

    .icon-success {
      color: var(--form-success-color);
    }

    .icon-confirm {
      color: var(--form-primary-color);
    }

    .dialog-content {
      color: var(--form-text-secondary);
      font-size: 16px;
      line-height: 1.5;
      margin-bottom: 20px;
    }

    .dialog-list {
      list-style-type: none;
      padding: 0;
      margin: 0;
    }

    .dialog-list-item {
      display: flex;
      align-items: center;
      padding: 8px 0;
      color: var(--form-text-secondary);
    }

    .list-item-icon {
      margin-right: 10px;
      color: var(--form-primary-color);
      font-size: 18px;
    }

    .dialog-actions {
      display: flex;
      justify-content: flex-end;
      margin-top: 16px;
      gap: 12px;
    }

    .btn-no {
      background-color: var(--form-cancel-hover-bg);
      color: var(--form-text-secondary);
    }

    .btn-yes {
      color: white;
    }

    .btn-info {
      background-color: var(--dashboard-info-color);
    }

    .btn-warning {
      background-color: var(--form-warning-color);
    }

    .btn-error {
      background-color: var(--form-error-color);
    }

    .btn-success {
      background-color: var(--form-success-color);
    }

    .btn-confirm {
      background-color: var(--form-primary-color);
    }

    .btn-ok {
      min-width: 100px;
    }
  `],
  template: `
    <div class="dialog-container">
      <h1 class="dialog-header">
        <mat-icon class="dialog-icon icon-{{ data.type }}">{{ getHeaderIcon() }}</mat-icon>
        {{ data.header }}
      </h1>
      
      <div class="dialog-content">
        <ng-container *ngIf="data.contentList && data.contentList.length > 0; else singleContent">
          <ul class="dialog-list">
            <li *ngFor="let message of data.contentList" class="dialog-list-item">
              <mat-icon class="list-item-icon icon-{{ data.type }}">check_circle</mat-icon>
              {{ message }}
            </li>
          </ul>
        </ng-container>
        <ng-template #singleContent>
          <p>{{ data.content }}</p>
        </ng-template>
      </div>
      
      <div class="dialog-actions">
        <button 
          *ngIf="!this.data.isInfo" 
          mat-raised-button 
          class="btn-no" 
          (click)="onNoClick()">
          Cancel
        </button>
        
        <button 
          mat-raised-button 
          [class]="this.data.isInfo ? 'btn-ok btn-yes btn-' + data.type : 'btn-yes btn-' + data.type" 
          (click)="onYesClick()">
          {{ this.data.isInfo ? 'OK' :this.data.buttonName? this.data.buttonName: 'Confirm' }}
        </button>
      </div>
    </div>
  `,
})
export class DialogComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { 
      header: string; 
      content: string;
      isInfo?: boolean;
      contentList?: string[];
      buttonName?:string;
      type?: 'info' | 'warning' | 'error' | 'confirm' | 'success'
    },
    private dialogRef: MatDialogRef<DialogComponent>
  ) {
    this.data.isInfo = this.data.isInfo ?? false;
    this.data.type = this.data.type ?? (this.data.isInfo ? 'info' : 'confirm');
  }

  getHeaderIcon(): string {
    switch (this.data.type) {
      case 'warning': return 'warning';
      case 'error': return 'error';
      case 'success': return 'check_circle';
      case 'info': return 'info';
      default: return 'help';
    }
  }

  onNoClick(): void {
    this.dialogRef.close(false);
  }

  onYesClick(): void {
    this.dialogRef.close(true);
  }
}